import {clone} from 'lodash';
import { createLogger } from 'lbs-pi-handy/src/general-purpose/logger';
import us from '../../defines';
import {getImageIdFromSrcAttr, isNumber} from '../../lib/utility/helpers';
import app from '../../public-api';
import {translate} from 'shared/services/I18nService';
import { makeVisibilityHelper } from '../viewport-overlay';
const $t = translate.scope('uScope.extensions.label');

const logger = createLogger('UScope:extensions:Label');

const defaults = {
  layout: {'divClass': us.clsDIVLABEL, 'precinct': 'TR', 'order': 1}
};

export default function Label (uScope, viewport, userConfig, visibilityPanel) {
  var isLoaded = false; // label has been loaded

  var jVpDiv = viewport.vpDivGet('outer');
  var jLabelDiv;

  var angleDeg = 0; // the label's angle of rotation in degrees

  var prefRotationDef = 0; // label rotation default

  var navModesImHiddenIn = {}; // what navigation modes not to display the label

  var layout = clone(defaults.layout);
  var reLayout = viewport.overLayout.refreshPrecinct;

  // ............................................ construct_ local
  var construct_ = function () {
    navModesImHiddenIn[us.eNavModes.nmRasterScan] = true; // hide label in rasterscan mode
    us.setAnnotNavModes(navModesImHiddenIn); // hide label when drawing any annotation

    // if the label has been disabled (TODO: or the user does not have rights to view it), don't load it
    // or if this image has no label, return
    if (!us.ENABLELABEL || !viewport.hasALabel()) {
      return;
    }

    load_(); // load the label image

    // ..................................................
    // closure functions
    // ............................................ load_()
    //    This function loads the label image div and, when the image loaded,
    //    calls into the layout manager to position it according to its layout settings.
    function load_ () {
      // if this viewport already has a label div, return
      if (jLabelDiv) {
        return;
      }

      // first create an offscreen div that will contain the Label img and add it to the vp
      jLabelDiv = $('<div/>', { // build the div tag that contains the img tag
        'class': us.clsDIVLABEL + ' ' + us.clsOVERLAY,
        'title': $t('image-label-title'), // tooltip
        css: {'left': us.kDEF_TILE_POS_PX, 'top': us.kDEF_TILE_POS_PX}
      });
      jVpDiv.append(jLabelDiv); // place the label div in the vp
      jLabelDiv = jVpDiv.find('.' + us.clsDIVLABEL);

      // next build the image tag and load the label image
      // BROWSER_FIX_HERE: the label is requested as a square so FF rotation leaves space in layout manger who doesn't know if it is rotated

      var sLabelImgSrc = viewport.urlBuilder.label(us.labelSpecs.width, us.labelSpecs.width);

      $('<img/>', { // place the labelImg in the DOM's label div
        load: function (e) {
          // check that this viewport's imageId === this image source's imageId
          var srcId = getImageIdFromSrcAttr(e.currentTarget); // format "@2"
          if (viewport.getImageId() !== srcId) { // if they don't match, do not load this thumbnaail (async return from another image!)
            logger.warn('...label:load: vpDiv id=' + viewport.getImageId() + '  srcId=' + srcId + ' - skipping load!!');
            return;
          }
          if (isLoaded) { // don't load the same label multiple times (happens when VIL reopens same image)
            logger.warn('...label:load: Label image for (' + viewport.getImageId() + ') received again!!');
            return;
          }
          isLoaded = true;

          jLabelDiv.append(e.currentTarget); // place the labelImg in the DOM's label div in the viewport
          viewport.overLayout.add(layout); // and call into layout with parms to place the div
          orientLabelPerUserPrefs();
        },
        error: function (e) {
          logger.error('label:load: error');
        },
        'class': us.clsIMGLABEL,
        'src': sLabelImgSrc,
        'alt': ''
      });
      bindEvents_(); // add event handlers
    }
    // ............................................ bindEvents_()
    // bindEvents_ - This function adds the  label event handlers.
    //
    //    Note that by using the same namespace as the viewport when the
    //    viewport removes its events by namespace, these are also removed.
    function bindEvents_ () {
      var namespace = viewport.eventNameSpaceGet();

      // user preference update
      makeVisibilityHelper(userConfig, us.clsDIVLABEL, viewport)
        .onVisibilityChanged(updateVisibility);

      userConfig.onLoad(orientLabelPerUserPrefs);
      userConfig.events.on(`change:labelRotation`, (value) => {
        rotate_(value, 'absolute');
      });
      $(us.idHashROOT).on(us.evUSERPREFDEFAULTLABELROT + namespace, function (e, parms) {
        userConfig.set('labelRotation', prefRotationDef);
      });

      // Add option to visibility panel
      visibilityPanel.addOption({
        key: us.clsDIVLABEL,
        label: translate('uScope.extensions.settings-service.visibility-panel.label')
      },
      7);

      // prevent dblclick on label causing double click zoom on viewport
      // capture mousedown so event does not propigate to viewport and close open dialogs
      jLabelDiv.on('mousedown' + namespace + ' dblclick' + namespace, function (e) {
        return false;
      });

      jLabelDiv.on('click' + namespace, function (e) {
        viewport.setImageSelected(); // inform uScope that this image is in focus
        rotate_(90, 'relative');
        e.preventDefault();
        return false;
      });
    }
    // ..................................................
  };

    // ............................................ get/set() public
  this.getViewAngle = function () {
    return angleDeg;
  };

  // ............................................ show() public
  function updateVisibility (show) {
    if (show) {
      jLabelDiv.show();
    } else {
      jLabelDiv.hide();
    }

    orientLabelPerUserPrefs();
    reLayout(layout.precinct); // layout just this overlay's precinct
  }

  // ............................................ rotate_() local
  //    This function rotates the label.
  //    parameters:
  //        nDegrees    : angle in degrees
  //        relOrAbs    : { "relative or "absolute" }
  //                        if "relative" the label is rotated the current angle + nDegrees clockwise
  //                        if "absolute" the label is rotated nDegrees clockwise
  //    returns:    none
  //    limitations - only 90 degree angles are implemented
  //
  //    improvements:
  //        - would it be better to have a circular array of rotation classes rather than build the string?
  //        - when tiled images are implemented this will have to select the proper image
  //
  // Label Rotation: http://answers.oreilly.com/topic/1004-how-to-rotate-an-image-with-css/
  //    http://www.communitymx.com/content/article.cfm?cid=6DA05
  //
  var rotate_ = function (nDegrees, relOrAbs) {
    if (!jLabelDiv || !isNumber(nDegrees) ||
        !us.ENABLELABEL || !isLoaded) {
      return;
    }

    if ((nDegrees % 90) != 0) {
      nDegrees = 0;
    }

    var curRotationClass = us.ROTATE + angleDeg;

    var newRotation = (relOrAbs == 'relative') ? (+angleDeg + nDegrees) % 360 : +nDegrees;
    var newRotationClass = us.ROTATE + newRotation;

    jLabelDiv.removeClass(curRotationClass).addClass(newRotationClass);
    publishRotateEvent(newRotation);

    angleDeg = newRotation;
  };

  function orientLabelPerUserPrefs () {
    rotate_(userConfig.get('labelRotation'), 'absolute');
  }

  function publishRotateEvent (newDegree) {
    const rotationDetails = {
      newDegree
    };
    app.events.trigger('us:label:rotation-changed', rotationDetails);
  }
  // ............................................ construction
  construct_();
}
